import { useState, forwardRef, useImperativeHandle, useId, useEffect, useRef } from 'react';
import { DynamicAddSectionProps, DynamicAddSectionRef, Entry, SectionType, FormError } from './DynamicAddSection.types';
import clsx from 'clsx';
import { InputTextField, Button, Types, Icon, Text } from '@marriott/mi-ui-library';
import { TextArea } from '../../atoms/TextArea';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { removeXSSScripts } from '../../util/helpers';
import { relationshipTypeDefaultOption, relationshipTypes, childrenAge } from '../../util/constants';
// import { useQuery } from '@apollo/client';
// import { phoenixPrearrivalProperty } from '../../graphql';
import { AccordionEnhanced } from '@marriott/mi-ui-library';
import { StyledDynamicSection } from './DynamicAddSection.styles';

export const DynamicAddSection = forwardRef<DynamicAddSectionRef, DynamicAddSectionProps>((props, ref) => {
  const inputId = useId();
  const { mode } = props;
  const { formData, setFormData } = useCreateSurveyStore();

  const [entries, setEntries] = useState<Entry[]>([]);

  const [customError, setCustomError] = useState<Record<string, string>>({});

  const [errors, setErrors] = useState<FormError>({
    errorTitle: '',
    errorFirstName: '',
    errorLastName: '',
    titleError: '',
    firstNameError: '',
    lastNameError: '',
    relationshipTypeError: '',
    errorChildAge: '',
    childFirstNameError: '',
    childLastNameError: '',
    childAgeError: '',
    childNotesAreaError: '',
    petNameError: '',
    petBreedError: '',
    petNotesAreaError: '',
    errorChildFirstName: '',
    errorChildLastName: '',
    errorPetName: '',
    errorPetBreed: '',
    errorPetNotesArea: '',
  });

  const errorMessage1 = 'Please limit your input to 255 characters.';

  const MAX_LIMITS = {
    guest: 500,
    child: 500,
    pet: 300,
  };

  const addSection = (type: SectionType) => {
    if (mode === 'guestChild') {
      if (type === 'guest') {
        const list = formData.additional_guests_details || [];

        if (list.length >= MAX_LIMITS.guest) return;

        const newIndex = list.length;

        const newGuest = {
          title: '',
          guest_first_name: '',
          guest_last_name: '',
          relationship_type: '',
        };

        setFormData({
          ...formData,
          additional_guests_details: [...list, newGuest],
        });

        setEntries(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'guest',
            number: newIndex + 1,
            dataIndex: newIndex,
          },
        ]);
      }

      if (type === 'child') {
        const list = formData.children_details || [];

        if (list.length >= MAX_LIMITS.child) return;

        const newIndex = list.length;

        const newChild = {
          childFirstName: '',
          childLastName: '',
          childAge: '',
          childNotesArea: '',
        };

        setFormData({
          ...formData,
          children_details: [...list, newChild],
        });

        setEntries(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'child',
            number: newIndex + 1,
            dataIndex: newIndex,
          },
        ]);
      }
    }

    if (mode === 'pet') {
      if (type === 'pet') {
        const list = formData.pet_details || [];

        if (list.length >= MAX_LIMITS.pet) return;

        const newIndex = list.length;

        const newPet = {
          petName: '',
          petBreed: '',
          petNotesArea: '',
        };

        setFormData({
          ...formData,
          pet_details: [...list, newPet],
        });

        setEntries(prev => [
          ...prev,
          {
            id: Date.now(),
            type: 'pet',
            number: newIndex + 1,
            dataIndex: newIndex,
          },
        ]);
      }
    }
  };

  const validate = (): boolean => {
    let isValid = true;

    entries.forEach(entry => {
      if (entry.type === 'guest') {
        const item = formData.additional_guests_details?.[entry.dataIndex];
        if (!validateLength(item?.guest_first_name || '', entry, 'firstNameError')) isValid = false;
        if (!validateLength(item?.guest_last_name || '', entry, 'lastNameError')) isValid = false;
      }
      if (entry.type === 'child') {
        const item = formData.children_details?.[entry.dataIndex];
        if (!validateLength(item?.childFirstName || '', entry, 'childFirstNameError')) isValid = false;
        if (!validateLength(item?.childLastName || '', entry, 'childLastNameError')) isValid = false;
      }
      if (entry.type === 'pet') {
        const item = formData.pet_details?.[entry.dataIndex];
        if (!validateLength(item?.petName || '', entry, 'petNameError')) isValid = false;
      }
    });

    return isValid;
  };

  useImperativeHandle(ref, () => ({
    addSection,
    validate,
  }));

  const validateRef = useRef(validate);
  useEffect(() => {
    validateRef.current = validate;
  });

  useEffect(() => {
    const handler = () => validateRef.current();
    window.addEventListener('validate-dynamic-section', handler);
    return () => window.removeEventListener('validate-dynamic-section', handler);
  }, []);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('text');
    const cleanData = removeXSSScripts(pasteData);

    return cleanData;
  };

  // const currentTimestamp = Date.now();
  // const currentDateTimeStamp = new Date(currentTimestamp).getTime();

  // const [skipQuery, setSkipQuery] = useState(false);

  // useQuery(phoenixPrearrivalProperty, {
  //   variables: {
  //     propertyId: marshaCode,
  //   },
  //   context: {
  //     headers: {
  //       // Any header, including x-request-id, can be passed in args with query.
  //       // If you don't pass it in the authLink will generate a random ID.
  //       'accept-language': 'en-US',
  //       'x-request-id': requestId,
  //       'x-b3-traceId': requestId,
  //       'x-b3-spanId': requestId,
  //       'correlation-id': `${sessionTokenKey} - ${currentDateTimeStamp}`,
  //       'graphql-operation-signature': `${operationSignatures}`,
  //     },
  //   },
  //   errorPolicy: 'all',
  //   onCompleted: data => {
  //     console.log('uxl data not coming');
  //     setPetFeeData(data);
  //     setSkipQuery(true);
  //   },
  //   onError: error => {
  //     console.error('Failed to get pet policy', error);
  //     setSkipQuery(true);
  //   },
  //   skip: skipQuery || !marshaCode,
  // });

  // -------------------------
  // SAVE GUEST BY INDEX
  // -------------------------

  const saveGuest = (dataIndex: number, field: string, value: string) => {
    const oldList = formData.additional_guests_details || [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const list = [...oldList];

    const item = {
      title: '',
      guest_first_name: '',
      guest_last_name: '',
      relationship_type: '',
      ...list[dataIndex],
    };

    item[field] = value;

    list[dataIndex] = item;

    setFormData({
      ...formData,
      additional_guests_details: list,
    });
  };

  // -------------------------
  // SAVE CHILD BY INDEX
  // -------------------------

  const saveChild = (dataIndex: number, field: string, value: string) => {
    const oldList = formData.children_details || [];

    const list = [...oldList];

    const item = {
      childFirstName: '',
      childLastName: '',
      childAge: '',
      childNotesArea: '',
      ...list[dataIndex],
    };

    item[field] = value;

    list[dataIndex] = item;

    setFormData({
      ...formData,
      children_details: list,
    });
  };

  // -------------------------
  // SAVE PET BY INDEX
  // -------------------------

  const savePet = (dataIndex: number, field: string, value: string) => {
    const oldList = formData.pet_details || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const list = [...oldList];

    const item = {
      petName: '',
      petBreed: '',
      petNotesArea: '',
      ...list[dataIndex],
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item[field] = value;

    list[dataIndex] = item;

    setFormData({
      ...formData,
      pet_details: list,
    });
  };

  useEffect(() => {
    const guestList = formData.additional_guests_details || [];
    const childList = formData.children_details || [];
    const petList = formData.pet_details || [];

    const newEntries: Entry[] = [];

    if (mode === 'guestChild') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      guestList.forEach((_: any, i: any) => {
        newEntries.push({
          id: Date.now() + i,
          type: 'guest',
          number: i + 1,
          dataIndex: i,
        });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      childList.forEach((_: any, i: any) => {
        newEntries.push({
          id: Date.now() + 100 + i,
          type: 'child',
          number: i + 1,
          dataIndex: i,
        });
      });
    }

    if (mode === 'pet') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      petList.forEach((_: any, i: any) => {
        newEntries.push({
          id: Date.now() + 200 + i,
          type: 'pet',
          number: i + 1,
          dataIndex: i,
        });
      });
    }

    setEntries(newEntries);
  }, [mode]);

  //--------------------------
  // Delete by index
  //--------------------------
  const deleteEntry = (entry: Entry) => {
    console.log('before delete', entry);

    if (entry.type === 'guest') {
      const list = [...(formData.additional_guests_details || [])];

      list.splice(entry.dataIndex, 1);

      setFormData({
        ...formData,
        additional_guests_details: list,
      });
    }

    if (entry.type === 'child') {
      const list = [...(formData.children_details || [])];

      list.splice(entry.dataIndex, 1);

      setFormData({
        ...formData,
        children_details: list,
      });
    }

    if (entry.type === 'pet') {
      const list = [...(formData.pet_details || [])];

      list.splice(entry.dataIndex, 1);

      setFormData({
        ...formData,
        pet_details: list,
      });
    }

    // remove accordion entry
    setEntries(prev => {
      const filtered = prev.filter(e => e.id !== entry.id);

      return filtered.map(e => {
        if (e.type === entry.type && e.dataIndex > entry.dataIndex) {
          return {
            ...e,
            dataIndex: e.dataIndex - 1,
            number: e.number - 1,
          };
        }

        return e;
      });
    });
  };

  const getErrorKey = (entry: Entry, errorKey: string) => `${entry.id}-${errorKey}`;

  const getFieldError = (entry: Entry, errorKey: string) => customError[getErrorKey(entry, errorKey)] || '';

  const validateLength = (value: string, entry: Entry, errorKey: string) => {
    const trimmed = value?.trim() || '';
    const scopedErrorKey = getErrorKey(entry, errorKey);

    // VALID CASES
    if (trimmed === '') {
      setCustomError(prev => ({
        ...prev,
        [scopedErrorKey]: 'This field is required',
      }));
      return false;
    }

    if (trimmed.length >= 255) {
      setCustomError(prev => ({
        ...prev,
        [scopedErrorKey]: errorMessage1,
      }));
      return false;
    }

    // INVALID CASE
    setCustomError(prev => ({
      ...prev,
      [scopedErrorKey]: '',
    }));

    return true;
  };

  const validateOnlyLength = (value: string, entry: Entry, errorKey: string) => {
    const trimmed = value?.trim() || '';
    const scopedErrorKey = getErrorKey(entry, errorKey);

    if (trimmed.length >= 255) {
      setCustomError(prev => ({
        ...prev,
        [scopedErrorKey]: errorMessage1,
      }));
      return false;
    }

    // INVALID CASE
    setCustomError(prev => ({
      ...prev,
      [scopedErrorKey]: '',
    }));

    return true;
  };

  // useEffect(() => {
  //   console.log('UPDATED customError:', customError);
  // }, [customError]);

  return (
    <StyledDynamicSection
      data-testid="dynamicAddSection"
      data-component-name="o-account-dynamicAddSection"
      className="d-flex flex-column dynamicSection"
    >
      <section className="dynamic-add-section">
        {/* Render Section */}
        {entries.map((entry, index) => (
          <AccordionEnhanced
            id={`accordion-${entry.id}`}
            isOpen={true}
            headerChildren={
              <div className="user-card">
                <div className="user-left">
                  <div className="avatar">
                    <Icon iconClass={'icon-nav---account-alt-active utility-icon'} />
                  </div>

                  <div className="user-info">
                    <div className="user-name">
                      <Text
                        element={Types.tags.paragraph}
                        fontSize={Types.size.medium}
                        copyText={(() => {
                          let text = '';
                          if (entry.type === 'guest') {
                            text = [
                              formData.additional_guests_details?.[entry.dataIndex]?.title,
                              formData.additional_guests_details?.[entry.dataIndex]?.guest_first_name,
                              formData.additional_guests_details?.[entry.dataIndex]?.guest_last_name,
                            ]
                              .filter(Boolean)
                              .join(' ');
                            if (!text) text = 'New Guest';
                          }

                          if (entry.type === 'child') {
                            text = [
                              formData.children_details?.[entry.dataIndex]?.childFirstName,
                              formData.children_details?.[entry.dataIndex]?.childLastName,
                            ]
                              .filter(Boolean)
                              .join(' ');
                            if (!text) text = 'New Child';
                          }

                          if (entry.type === 'pet') {
                            text = [formData.pet_details?.[entry.dataIndex]?.petName].filter(Boolean).join(' ');
                            if (!text) text = 'New Pet';
                          }
                          return text;
                        })()}
                      />
                      <span className="check">
                        {(() => {
                          if (entry.type === 'guest') {
                            const item = formData.additional_guests_details?.[entry.dataIndex];

                            const isValid = item?.guest_first_name?.trim() && item?.guest_last_name?.trim();

                            return isValid ? <Icon iconClass="icon-check" /> : <Icon iconClass="icon-alert" />;
                          }

                          if (entry.type === 'child') {
                            const item = formData.children_details?.[entry.dataIndex];

                            const isValid = item?.childFirstName?.trim() && item?.childLastName?.trim();

                            return isValid ? <Icon iconClass="icon-check" /> : <Icon iconClass="icon-alert" />;
                          }

                          if (entry.type === 'pet') {
                            const item = formData.pet_details?.[entry.dataIndex];

                            const isValid = item?.petName?.trim();

                            return isValid ? <Icon iconClass="icon-check" /> : <Icon iconClass="icon-alert" />;
                          }

                          return null;
                        })()}
                      </span>
                    </div>

                    <div className="user-sub">
                      <Text
                        element={Types.tags.paragraph}
                        fontSize={Types.size.extraSmall}
                        copyText={(() => {
                          let text = '';
                          if (entry.type === 'guest') {
                            text =
                              'Adult \u25CF ' +
                              [formData.additional_guests_details?.[entry.dataIndex]?.relationship_type]
                                .filter(Boolean)
                                .join('');
                            if (!formData.additional_guests_details?.[entry.dataIndex]?.relationship_type)
                              text = 'Adult \u25CF No Relationship Selected';
                          }

                          if (entry.type === 'child') {
                            text =
                              'Age \u25CF ' +
                              [formData.children_details?.[entry.dataIndex]?.childAge].filter(Boolean).join('');
                            if (!formData.children_details?.[entry.dataIndex]?.childAge)
                              text = 'Age \u25CF No Age Selected';
                          }

                          if (entry.type === 'pet') {
                            text =
                              'Breed \u25CF ' +
                              [formData.pet_details?.[entry.dataIndex]?.petBreed].filter(Boolean).join('');
                            if (!formData.pet_details?.[entry.dataIndex]?.petBreed)
                              text = 'Breed \u25CF No Breed Selected';
                          }
                          return text;
                        })()}
                      />
                    </div>
                  </div>
                </div>

                <div className="user-right">
                  {(() => {
                    if (entry.type === 'guest') {
                      const item = formData.additional_guests_details?.[entry.dataIndex];

                      const isValid = item?.guest_first_name?.trim() && item?.guest_last_name?.trim();

                      return isValid ? (
                        <span className="saved">Saved</span>
                      ) : (
                        <span className="missing-info">Missing info</span>
                      );
                    }

                    if (entry.type === 'child') {
                      const item = formData.children_details?.[entry.dataIndex];

                      const isValid = item?.childFirstName?.trim() && item?.childLastName?.trim();

                      return isValid ? (
                        <span className="saved">Saved</span>
                      ) : (
                        <span className="missing-info">Missing info</span>
                      );
                    }

                    if (entry.type === 'pet') {
                      const item = formData.pet_details?.[entry.dataIndex];

                      const isValid = item?.petName?.trim();

                      return isValid ? (
                        <span className="saved">Saved</span>
                      ) : (
                        <span className="missing-info">Missing info</span>
                      );
                    }

                    return null;
                  })()}

                  <Button
                    className="acc-delete-btn"
                    type={Types.ButtonTypeVariation.Button}
                    callback={() => deleteEntry(entry)}
                    children={<Icon iconClass="icon-delete" />}
                  ></Button>
                </div>
              </div>
            }
            disableAccResize={false}
            assetVariation={'iconFont'}
            iconPath={'icon-spa'}
            children={
              <div key={entry.id} className="">
                {entry.type === 'guest' && (
                  <div className=" col-12 col-xl-10 px-0  col-12 col-xl-10 px-0">
                    <div className="d-flex justify-content-between flex-row-desktop">
                      <InputTextField
                        label="title"
                        placeHolderText={'Title'}
                        inputValue={removeXSSScripts(
                          formData.additional_guests_details?.[entry.dataIndex]?.title || ''
                        )}
                        showErrorMessage={!!getFieldError(entry, 'titleError')}
                        messageToShow={getFieldError(entry, 'titleError') || ''}
                        setErrorHtml={true}
                        messageClass="error-label"
                        getInputProps={() => ({
                          autoComplete: 'off',
                          id: `${inputId}-title`,
                          onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                        })}
                        getLabelProps={() => ({
                          htmlFor: `${inputId}-title`,
                        })}
                        inputMaxLength={255}
                        className={clsx(
                          'm-input-field mb-3',
                          getFieldError(entry, 'titleError')
                            ? 'is-error'
                            : formData.additional_guests_details?.[index]?.title
                            ? 'is-active'
                            : ''
                        )}
                        getInputValue={(val: string) => {
                          validateOnlyLength(val, entry, 'titleError');
                          saveGuest(entry.dataIndex, 'title', removeXSSScripts(val));
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onBlur={(val: any) => {
                          const newValue = removeXSSScripts(val.target.value);
                          validateOnlyLength(newValue, entry, 'titleError');
                          saveGuest(entry.dataIndex, 'title', newValue);
                        }}
                        infoLabel="title"
                      />
                      <div className="m-form-group mb-3 d-none d-md-block custom-select-wrapper additionalDeskSel col-md-6 p-0">
                        <select
                          id={`${inputId}-relationship_type`}
                          name="relationship_type"
                          value={formData.additional_guests_details?.[entry.dataIndex]?.relationship_type || ''}
                          onChange={e => {
                            // setLocalFormDataList({ ...localFormDataList, relationship_type: e.target.value });
                            saveGuest(entry.dataIndex, 'relationship_type', e.target.value);
                          }}
                          className={`selectBox selectBox-xl relSelect ${
                            errors?.relationshipTypeError ? 'is-error' : ''
                          }`}
                          required
                        >
                          <option value={relationshipTypeDefaultOption.value}>
                            {relationshipTypeDefaultOption.label}
                          </option>
                          {relationshipTypes.map((option, index) => (
                            <option key={index} value={option.value} role="option">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between flex-row-desktop">
                      <InputTextField
                        label="guest_first_name"
                        placeHolderText={'First Name *'}
                        inputValue={removeXSSScripts(
                          formData.additional_guests_details?.[entry.dataIndex]?.guest_first_name || ''
                        )}
                        showErrorMessage={!!getFieldError(entry, 'firstNameError')}
                        messageToShow={getFieldError(entry, 'firstNameError') || ''}
                        setErrorHtml={true}
                        inputMaxLength={255}
                        messageClass="error-label"
                        getInputProps={() => ({
                          autoComplete: 'off',
                          id: `${inputId}-guest_first_name`,
                          onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                        })}
                        getLabelProps={() => ({
                          htmlFor: `${inputId}-guest_first_name`,
                        })}
                        className={clsx(
                          'm-input-field is-active mb-3',
                          getFieldError(entry, 'firstNameError')
                            ? 'is-error'
                            : formData.additional_guests_details?.[entry.dataIndex]?.guest_first_name
                            ? 'is-active'
                            : ''
                        )}
                        getInputValue={(val: string) => {
                          validateLength(val, entry, 'firstNameError');
                          saveGuest(entry.dataIndex, 'guest_first_name', removeXSSScripts(val));
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onBlur={(val: any) => {
                          const newValue = removeXSSScripts(val.target.value);
                          validateLength(newValue, entry, 'firstNameError');
                          saveGuest(entry.dataIndex, 'guest_first_name', removeXSSScripts(newValue));
                        }}
                        infoLabel="first-name"
                      />
                      <div className="m-form-group mb-3 d-md-block additionalDeskSel col-md-6 p-0">
                        <InputTextField
                          label="guest_last_name"
                          placeHolderText={'Last Name *'}
                          inputValue={removeXSSScripts(
                            formData.additional_guests_details?.[entry.dataIndex]?.guest_last_name || ''
                          )}
                          showErrorMessage={!!getFieldError(entry, 'lastNameError')}
                          messageToShow={getFieldError(entry, 'lastNameError') || ''}
                          setErrorHtml={true}
                          messageClass="error-label"
                          inputMaxLength={255}
                          getInputProps={() => ({
                            autoComplete: 'off',
                            // onKeyUp: handleKeyPress,
                            id: `${inputId}-guest_last_name`,
                            onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                          })}
                          getLabelProps={() => ({
                            htmlFor: `${inputId}-guest_last_name`,
                          })}
                          className={clsx(
                            'm-input-field is-active',
                            getFieldError(entry, 'lastNameError')
                              ? 'is-error'
                              : formData.additional_guests_details?.[entry.dataIndex]?.guest_last_name
                              ? 'is-active'
                              : ''
                          )}
                          getInputValue={(val: string) => {
                            validateLength(val, entry, 'lastNameError');
                            saveGuest(entry.dataIndex, 'guest_last_name', removeXSSScripts(val));
                          }}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onBlur={(val: any) => {
                            const newValue = removeXSSScripts(val.target.value);
                            validateLength(newValue, entry, 'lastNameError');
                            saveGuest(entry.dataIndex, 'guest_last_name', removeXSSScripts(newValue));
                          }}
                          infoLabel="last-name"
                        />
                      </div>
                    </div>
                    <div className="m-form-group mb-3 d-block d-md-none custom-select-wrapper additionalSelect ">
                      <select
                        id={`${inputId}-relationship_type`}
                        name="relationship_type"
                        value={formData.additional_guests_details?.[entry.dataIndex]?.relationship_type || ''}
                        onChange={e => {
                          // setLocalFormDataList({ ...localFormDataList, relationship_type: e.target.value });
                          saveGuest(entry.dataIndex, 'relationship_type', e.target.value);
                        }}
                        className={`selectBox selectBox-xl relSelect ${
                          errors?.relationshipTypeError ? 'is-error' : ''
                        }`}
                        required
                      >
                        <option value={relationshipTypeDefaultOption.value}>
                          {relationshipTypeDefaultOption.label}
                        </option>
                        {relationshipTypes.map((option, index) => (
                          <option key={index} value={option.value} role="option">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {entry.type === 'child' && (
                  <div className=" col-12 col-xl-10 px-0  col-12 col-xl-10 px-0">
                    <div className="d-flex justify-content-between flex-row-desktop">
                      <InputTextField
                        label="childFirstName"
                        placeHolderText={'First Name *'}
                        inputValue={removeXSSScripts(
                          formData.children_details?.[entry.dataIndex]?.childFirstName || ''
                        )}
                        showErrorMessage={!!getFieldError(entry, 'childFirstNameError')}
                        messageToShow={getFieldError(entry, 'childFirstNameError') || ''}
                        setErrorHtml={true}
                        inputMaxLength={255}
                        messageClass="error-label"
                        getInputProps={() => ({
                          autoComplete: 'off',
                          id: `${inputId}-childFirstName`,
                          onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                        })}
                        getLabelProps={() => ({
                          htmlFor: `${inputId}-childFirstName`,
                        })}
                        className={clsx(
                          'm-input-field mb-3',
                          getFieldError(entry, 'childFirstNameError')
                            ? 'is-error'
                            : formData.children_details?.[entry.dataIndex]?.childFirstName
                            ? 'is-active'
                            : ''
                        )}
                        getInputValue={(val: string) => {
                          validateLength(val, entry, 'childFirstNameError');
                          saveChild(entry.dataIndex, 'childFirstName', removeXSSScripts(val));
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onBlur={(val: any) => {
                          const newValue = val.target.value;
                          validateLength(newValue, entry, 'childFirstNameError');
                          saveChild(entry.dataIndex, 'childFirstName', removeXSSScripts(newValue));
                        }}
                        infoLabel="childFirstName"
                      />
                      <InputTextField
                        label="childLastName"
                        placeHolderText={'Last Name *'}
                        inputValue={removeXSSScripts(formData.children_details?.[entry.dataIndex]?.childLastName || '')}
                        showErrorMessage={!!getFieldError(entry, 'childLastNameError')}
                        messageToShow={getFieldError(entry, 'childLastNameError') || ''}
                        setErrorHtml={true}
                        inputMaxLength={255}
                        messageClass="error-label"
                        getInputProps={() => ({
                          autoComplete: 'off',
                          id: `${inputId}-childLastName`,
                          onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                        })}
                        getLabelProps={() => ({
                          htmlFor: `${inputId}-childLastName`,
                        })}
                        className={clsx(
                          'm-input-field mb-3',
                          getFieldError(entry, 'childLastNameError')
                            ? 'is-error'
                            : formData.children_details?.[entry.dataIndex]?.childLastName
                            ? 'is-active'
                            : ''
                        )}
                        getInputValue={(val: string) => {
                          validateLength(val, entry, 'childLastNameError');
                          saveChild(entry.dataIndex, 'childLastName', removeXSSScripts(val));
                        }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onBlur={(val: any) => {
                          const newValue = val.target.value;
                          validateLength(newValue, entry, 'childLastNameError');
                          saveChild(entry.dataIndex, 'childLastName', removeXSSScripts(newValue));
                        }}
                        infoLabel="childLastName"
                      />
                    </div>
                    <div className="d-flex justify-content-between flex-row-desktop">
                      <div className="custom-select-wrapper additionalSelect col-md-6 p-0 mb-3">
                        <select
                          id={`${inputId}-childAge`}
                          name="childAge"
                          value={formData.children_details?.[entry.dataIndex]?.childAge}
                          onChange={e => {
                            saveChild(entry.dataIndex, 'childAge', e.target.value);
                          }}
                          className={`selectBox selectBox-xl`}
                        >
                          <option value="" disabled selected hidden>
                            {'Age'}
                          </option>
                          {childrenAge.map((option, index) => (
                            <option key={index} value={option.value} role="option">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 textarea-section mb-4 p-0">
                        <TextArea
                          id={`${inputId}-childNotesArea`}
                          key="childNotesArea"
                          name="childNotesArea"
                          label="childNotesArea"
                          placeHolderText={'Please add notes here'}
                          getInputValue={(val: string) => {
                            saveChild(entry.dataIndex, 'childNotesArea', val);
                          }}
                          maxLength={255}
                          messageError={errorMessage1}
                          inputValue={removeXSSScripts(formData.children_details?.[entry.dataIndex]?.childNotesArea)}
                          className={errors?.childNotesAreaError ? 'is-error' : ''}
                        ></TextArea>
                      </div>
                    </div>
                  </div>
                )}

                {entry.type === 'pet' && (
                  <div className="col-md-6 p-0 mb-3">
                    <InputTextField
                      label="petName"
                      placeHolderText={'Pet Name *'}
                      inputValue={removeXSSScripts(formData.pet_details?.[entry.dataIndex]?.petName)}
                      showErrorMessage={!!getFieldError(entry, 'petNameError')}
                      messageToShow={getFieldError(entry, 'petNameError') || ''}
                      setErrorHtml={true}
                      inputMaxLength={255}
                      messageClass="error-label"
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `${inputId}-petName`,
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                      })}
                      getLabelProps={() => ({
                        htmlFor: `${inputId}-petName`,
                      })}
                      className={clsx(
                        'm-input-field mb-3',
                        getFieldError(entry, 'petNameError')
                          ? 'is-error'
                          : formData.pet_details?.[entry.dataIndex]?.petName
                          ? 'is-active'
                          : ''
                      )}
                      getInputValue={(val: string) => {
                        validateLength(val, entry, 'petNameError');
                        savePet(entry.dataIndex, 'petName', removeXSSScripts(val));
                      }}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onBlur={(val: any) => {
                        const newValue = removeXSSScripts(val.target.value);
                        validateLength(newValue, entry, 'petNameError');
                        savePet(entry.dataIndex, 'petName', removeXSSScripts(newValue));
                      }}
                      infoLabel="pet-name"
                    />
                    <InputTextField
                      label="petBreed"
                      placeHolderText={'Pet Breed'}
                      inputValue={removeXSSScripts(formData.pet_details?.[entry.dataIndex]?.petBreed)}
                      showErrorMessage={!!getFieldError(entry, 'petBreedError')}
                      messageToShow={getFieldError(entry, 'petBreedError')}
                      setErrorHtml={true}
                      inputMaxLength={255}
                      messageClass="error-label"
                      getInputProps={() => ({
                        autoComplete: 'off',
                        id: `${inputId}-petBreed`,
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e),
                      })}
                      getLabelProps={() => ({
                        htmlFor: `${inputId}-petBreed`,
                      })}
                      className={clsx(
                        'm-input-field mb-3',
                        getFieldError(entry, 'petBreedError')
                          ? 'is-error'
                          : formData.pet_details?.[entry.dataIndex]?.petBreed
                          ? 'is-active'
                          : ''
                      )}
                      getInputValue={(val: string) => {
                        validateOnlyLength(val, entry, 'petBreedError');
                        savePet(entry.dataIndex, 'petBreed', removeXSSScripts(val));
                      }}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onBlur={(val: any) => {
                        const newValue = removeXSSScripts(val.target.value);
                        validateOnlyLength(newValue, entry, 'petBreedError');
                        savePet(entry.dataIndex, 'petBreed', removeXSSScripts(newValue));
                      }}
                      infoLabel="pet-breed"
                    />
                    <TextArea
                      id={`${inputId}-petNotesArea`}
                      key="petNotesArea"
                      name="petNotesArea"
                      label="petNotesArea"
                      placeHolderText="Please add notes here"
                      getInputValue={(val: string) => {
                        if (val?.length >= 255) {
                          setErrors({ ...errors, petNotesAreaError: errorMessage1 });
                        } else {
                          setErrors({ ...errors, petNotesAreaError: '' });
                        }
                        savePet(entry.dataIndex, 'petNotesArea', removeXSSScripts(val));
                      }}
                      maxLength={255}
                      messageError={errorMessage1}
                      inputValue={removeXSSScripts(formData.pet_details?.[entry.dataIndex]?.petNotesArea)}
                      className={errors?.petNotesAreaError ? 'is-error' : ''}
                    ></TextArea>
                  </div>
                )}
              </div>
            }
            customClass={(() => {
              if (entry.type === 'guest') {
                const item = formData.additional_guests_details?.[entry.dataIndex];

                const isValid = item?.guest_first_name?.trim() && item?.guest_last_name?.trim();

                return isValid ? 'success-msg' : 'alert-msg';
              }

              if (entry.type === 'child') {
                const item = formData.children_details?.[entry.dataIndex];

                const isValid = item?.childFirstName?.trim() && item?.childLastName?.trim();

                return isValid ? 'success-msg' : 'alert-msg';
              }

              if (entry.type === 'pet') {
                const item = formData.pet_details?.[entry.dataIndex];

                const isValid = item?.petName?.trim();

                return isValid ? 'success-msg' : 'alert-msg';
              }

              return 'alert-msg';
            })()}
          />
        ))}
      </section>
    </StyledDynamicSection>
  );
});

export default DynamicAddSection;
